import { useState, useEffect } from 'react'
import supabase from '../supabase/supabase'

export const useLoans = (userId = null, includeReturned = false) => {
  const [loans, setLoans] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchLoans()
  }, [userId, includeReturned])

  const fetchLoans = async () => {
    try {
      setLoading(true)
      let query = supabase
        .from('loans')
        .select(`
          *,
          books (
            id,
            title,
            author,
            isbn,
            cover_image
          ),
          profiles (
            full_name,
            email
          )
        `)

      if (userId) {
        query = query.eq('user_id', userId)
      }

      if (!includeReturned) {
        query = query.is('returned_date', null)
      }

      const { data, error } = await query.order('checkout_date', { ascending: false })

      if (error) throw error

      // Process loans to include status
      const processedLoans = data.map(loan => {
        const dueDate = new Date(loan.due_date)
        const now = new Date()
        const isOverdue = !loan.returned_date && dueDate < now
        const isDueSoon = !loan.returned_date && dueDate <= new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000)
        
        return {
          ...loan,
          isOverdue,
          isDueSoon,
          daysUntilDue: Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24))
        }
      })

      setLoans(processedLoans)
    } catch (error) {
      setError(error.message)
      console.error('Error fetching loans:', error)
    } finally {
      setLoading(false)
    }
  }

  const extendLoan = async (loanId, additionalDays = 7) => {
    try {
      const { data: loan, error: fetchError } = await supabase
        .from('loans')
        .select('due_date')
        .eq('id', loanId)
        .single()

      if (fetchError) throw fetchError

      const newDueDate = new Date(loan.due_date)
      newDueDate.setDate(newDueDate.getDate() + additionalDays)

      const { error: updateError } = await supabase
        .from('loans')
        .update({ due_date: newDueDate.toISOString() })
        .eq('id', loanId)

      if (updateError) throw updateError

      await fetchLoans()
      return { success: true }
    } catch (error) {
      console.error('Error extending loan:', error)
      return { success: false, error: error.message }
    }
  }

  return { loans, loading, error, extendLoan, refetch: fetchLoans }
}